import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { patchServiceDetails } from "services/services/serviceDetails/detail";
import { showError } from "../../../../../redux/common/actions";
import { parseDate, calculateEndDate } from "../utils/dateUtils";
import { getServiceTimeBreakdown } from "../utils/serviceTime";

// Marks the bar moves this hook makes itself, so they are not treated as drops
const REVERT_SOURCE = "reschedule-revert";
const ADJUST_SOURCE = "reschedule-adjust";

export const buildScheduledDatePatch = (start) => [
	{ op: "replace", path: "scheduledDate", value: start.toISOString() },
];

/**
 * Restores the bar to the dates the server still holds.
 *
 * serviceData carries the record as it was last loaded, so it stays the right
 * thing to fall back to: dragging moves the library's copy of the task, never
 * the service the bar was built from.
 */
export const getServerDates = (service) => {
	const start = parseDate(service?.scheduledDate);
	if (!start) return null;

	const end =
		calculateEndDate(
			service.scheduledDate,
			getServiceTimeBreakdown(service).total,
		) || start;

	return { start, end };
};

/**
 * Holds a dropped service until the user confirms its new start, then saves it.
 *
 * The library owns the bar, so the drop is already applied on screen by the
 * time this runs. Cancelling or a rejected save therefore has to put the bar
 * back rather than simply not move it.
 */
const useGanttReschedule = ({ ganttApi, onRescheduled }) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const [pending, setPending] = useState(null);

	// Task ids restart from 1 in a new Gantt, so a drop held from the old one
	// must not be confirmed against it
	useEffect(() => {
		setPending(null);
	}, [ganttApi]);

	useEffect(() => {
		if (!ganttApi) return undefined;

		// Summary rescoping and our own moves both carry an eventSource; a
		// committed drag is the only update that arrives without one
		const unsubscribe = ganttApi.on("update-task", (ev) => {
			if (ev.eventSource) return;

			const task = ganttApi.getTask(ev.id);
			const service = task?.serviceData;
			if (!service || !task.start) return;

			setPending({
				taskId: ev.id,
				service,
				workOrder: task.workOrder,
				role: task.role,
				start: task.start,
				end: task.end || task.start,
			});
		});

		return () => {
			if (typeof unsubscribe === "function") unsubscribe();
		};
	}, [ganttApi]);

	const revert = useCallback(
		({ taskId, service }) => {
			const dates = getServerDates(service);
			if (!dates) return;

			ganttApi.exec("update-task", {
				id: taskId,
				task: dates,
				eventSource: REVERT_SOURCE,
			});
		},
		[ganttApi],
	);

	const cancel = useCallback(() => {
		if (!pending) return;
		revert(pending);
		setPending(null);
	}, [pending, revert]);

	const confirm = useCallback(
		async (start) => {
			if (!pending) return;
			const { taskId, service } = pending;

			if (start.getTime() !== pending.start.getTime()) {
				ganttApi.exec("update-task", {
					id: taskId,
					task: {
						start,
						end: new Date(
							start.getTime() + pending.end.getTime() - pending.start.getTime(),
						),
					},
					eventSource: ADJUST_SOURCE,
				});
			}

			const fail = (detail) => {
				dispatch(
					showError(
						detail ||
							t("message.couldNotUpdateField", {
								field: t("label.scheduledDate"),
							}),
					),
				);
				revert(pending);
			};

			try {
				const response = await patchServiceDetails(
					service.id,
					buildScheduledDatePatch(start),
				);

				if (response?.status) {
					// The response echoes the stored value; fall back to what we sent
					onRescheduled(
						service.id,
						response.data?.scheduledDate || start.toISOString(),
					);
				} else {
					fail(response?.data?.detail);
				}
			} catch (error) {
				fail(error?.data?.detail);
			} finally {
				setPending(null);
			}
		},
		[pending, ganttApi, revert, onRescheduled, dispatch, t],
	);

	return { pending, confirm, cancel };
};

export default useGanttReschedule;
